import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { initiateShineConnection } from '../query-api'

interface ShineInitiateConnectionError {
  title?: string
  detail?: string
  status?: number
  type?: string
  instance?: string
}

export interface UseInitiateShineConnectionPayload {
  organizationId: string
  globalOrgId: string
  globalUserId: string
}

export const useInitiateShineConnection = () => {
  const { t } = useTranslation()
  const { user } = useCurrentUser()
  const { organization } = useUserOrganization()

  const {
    mutate: initiateShineConnectionMutate,
    isLoading: isProcessing,
    ...rest
  } = useMutation(
    ({ organizationId, globalOrgId, globalUserId }: UseInitiateShineConnectionPayload) =>
      initiateShineConnection({
        organizationId,
        params: {
          globalOrgId,
          globalUserId,
          callback: `${window.location.origin}/product/shine/callback`,
        },
      }),
    {
      onSuccess: (response) => {
        if (response?.redirectUrl) {
          window.location.replace(response?.redirectUrl)
          return
        }
        trackError('Shine connection: Missing redirectUrl in response')
      },
      onError: (error: ShineInitiateConnectionError) => {
        notify({
          variant: 'error',
          id: NotificationKeys.ShineInitiateConnectionError,
          message: error?.detail || t('bank_connection.shine_initiate_connection.error'),
        })
      },
    },
  )

  const initiateShineConnectionCallback = useCallback(() => {
    if (!organization || !organization.globalId || !user) {
      return
    }

    const initiatePayload: UseInitiateShineConnectionPayload = {
      organizationId: organization.id,
      globalOrgId: organization.globalId,
      globalUserId: user.globalId,
    }

    initiateShineConnectionMutate(initiatePayload)
  }, [initiateShineConnectionMutate, organization, user])

  return {
    isProcessing,
    initiateShineConnection: initiateShineConnectionCallback,
    ...rest,
  }
}
